class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * The last rendered section HTML, kept so the gender-matched list can be re-picked without a fetch
   * @type {string | null}
   */
  #lastResult = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    document.addEventListener('bw:model-change', this.#onModelChange);
  }

  disconnectedCallback() {
    document.removeEventListener('bw:model-change', this.#onModelChange);
  }

  /**
   * Unisex products with model galleries (e.g. Trawler): switching MEN / WOMEN switches the list too.
   */
  #onModelChange = () => {
    if (this.dataset.bwGender !== 'unisex' || !this.#lastResult) return;
    this.#render(this.#lastResult);
  };

  /**
   * Which list to show: the product's own gender, or on unisex products the model gallery shown
   * (?model=men|women). Anything else (kids, untagged, unisex without galleries) shows the original list.
   * @returns {'men' | 'women' | 'all'}
   */
  #audience() {
    const gender = this.dataset.bwGender;
    if (gender === 'men' || gender === 'women') return gender;

    const model = window.BWModelGallery?.model;
    if (gender === 'unisex' && (model === 'men' || model === 'women')) return model;

    return 'all';
  }

  /**
   * Shows the section's recommendations, keeping only the list for this page's audience
   * (sections/product-recommendations.liquid renders 'all', 'men' and 'women').
   * @param {string} result - The section HTML
   * @returns {boolean} Whether there was anything to show
   */
  #render(result) {
    // An inert template, so images in the lists that are dropped are never downloaded.
    const html = document.createElement('template');
    html.innerHTML = result;
    const recommendations = html.content.querySelector(`product-recommendations[id="${this.id}"]`);
    if (!recommendations?.innerHTML || !recommendations.innerHTML.trim().length) return false;

    const audience = this.#audience();
    const lists = recommendations.querySelectorAll('[data-bw-list]');
    const hasAudienceList = [...lists].some((list) => list.getAttribute('data-bw-list') === audience);

    // Lists are removed before insertion so only the shown carousel initialises.
    lists.forEach((list) => {
      const name = list.getAttribute('data-bw-list');
      if (name === (hasAudienceList ? audience : 'all')) {
        list.classList.add('bw-rec-list--active');
      } else {
        list.remove();
      }
    });

    recommendations.querySelectorAll('script[data-bw-model-images]').forEach((script) => {
      this.#applyModelImages(script);
    });

    this.#lastResult = result;
    this.innerHTML = recommendations.innerHTML;
    return true;
  }

  /**
   * Cards of products with model galleries render the shop's default model; in a men's or women's list,
   * swap in that model's photos and point the card at it (assets/model-gallery-links.js adds ?model=).
   * @param {HTMLScriptElement} script - JSON { model, images: [{ src, srcset }] } next to the card
   */
  #applyModelImages(script) {
    const item = script.closest('.resource-list__item');
    const marker = item?.querySelector('[data-mg-card-model]');
    if (!marker) return;

    let data;
    try {
      data = JSON.parse(script.textContent || '');
    } catch {
      return;
    }

    if (marker.getAttribute('data-mg-card-model') !== data.model) {
      item.querySelectorAll('slideshow-slide:not([hidden]) img').forEach((img, index) => {
        const image = data.images[index];
        if (!image) return;
        img.srcset = image.srcset;
        img.src = image.src;
      });
    }

    marker.setAttribute('data-mg-card-model', data.model);
    marker.setAttribute('data-mg-card-source', 'collection');
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (this.#render(result.data || '')) {
          this.dataset.recommendationsPerformed = 'true';
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}